import { Map as IMap } from 'immutable'
import { Consignment, ConsignmentEvent, RoutePoint } from '../types/coreEntitiesTypes'
import type { AppStateType } from '../utils/appStateReduxStore'
import { payloadOf } from './entities'
import { createIndex, createIndexes, EntitiesState } from './entityIndex'

/**
 * Which lookups the entities imply, maintained alongside them so a reader does not have to scan.
 * Adding one is a line here; `entityIndex.ts` holds how they are built and kept in step.
 */
const registry = createIndexes({
  consignmentsBySlotId: createIndex('consignments', (consignment: Consignment) => consignment.get('slotId')),
  routePointsBySlotId: createIndex('routePoints', (routePoint: RoutePoint) =>
    routePoint.get('ignore') ? undefined : routePoint.get('slotId')
  ),
  consignmentEventsByConsignmentId: createIndex('consignmentEvents', (event: ConsignmentEvent) =>
    event.get('consignmentId')
  ),
  consignmentEventsBySlotId: createIndex('consignmentEvents', (event: ConsignmentEvent) => event.get('slotId'))
})

export type IndexesState = typeof registry.empty

export const emptyIndexes = registry.empty

export const buildIndexes = registry.build

export const INDEXES_REDUCER_KEY = 'indexes'

/**
 * `AppStateType` describes only a handful of the store's slices, so the indexes are reached
 * through these two rather than by declaring them there and forcing every loosely-typed state
 * in the tests to account for a value that is not Immutable.
 */
const entitiesOf = (state: AppStateType | undefined): EntitiesState =>
  state?.get('entities') as unknown as EntitiesState

export const indexesOf = (state: AppStateType | undefined): IndexesState =>
  ((state as unknown as IMap<string, unknown> | undefined)?.get(INDEXES_REDUCER_KEY) as IndexesState | undefined) ??
  emptyIndexes

type IndexedAction = Readonly<{
  type: string
  data?: unknown
  body?: object
  isEntity?: boolean
}>

export const updateIndexes = (
  indexes: IndexesState,
  previousEntities: EntitiesState,
  nextEntities: EntitiesState,
  action: IndexedAction
): IndexesState => registry.update(indexes, previousEntities, nextEntities, payloadOf(action))

export const indexesReducer = (state: IndexesState = emptyIndexes): IndexesState => state

/**
 * Keeps the indexes in step with whatever the entities reducer just wrote. Runs in the root
 * reducer rather than as a slice because it needs the entities from before and after the write.
 */
export const withIndexes = (
  previousState: AppStateType | undefined,
  nextState: AppStateType,
  action: IndexedAction
): AppStateType => {
  const previousIndexes = indexesOf(previousState)
  const nextIndexes = updateIndexes(previousIndexes, entitiesOf(previousState), entitiesOf(nextState), action)
  return nextIndexes === previousIndexes
    ? nextState
    : ((nextState as unknown as IMap<string, unknown>).set(INDEXES_REDUCER_KEY, nextIndexes) as unknown as AppStateType)
}
